import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { PageHeader, Card, Btn, Modal, Field, Input, Select, Badge, EmptyState, StatTile } from "../components/kr";
import { DatePicker } from "../components/DatePicker";
import { MoneyInput } from "../components/MoneyInput";
import { useConfirm } from "../components/ConfirmDialog";
import { useAuth } from "../lib/auth";
import { useMultiSelect } from "../lib/multiSelect";
import MultiSelectBar from "../components/MultiSelectBar";
import { toast } from "sonner";
import { Plus, Trash, PencilSimple, ArrowSquareOut, Repeat, WarningCircle } from "@phosphor-icons/react";

const daysUntil = (date) => {
  const diff = new Date(date) - new Date();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
};

export default function Subscriptions() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(null);
  const { confirm } = useConfirm();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const ms = useMultiSelect();

  const load = async () => setItems((await api.get("/subscriptions")).data);
  useEffect(() => { load(); }, []);

  const del = async (i) => {
    const ok = await confirm({
      title: "Delete subscription?",
      message: `${i.name} · ₹${i.amount.toLocaleString("en-IN")}/${i.cycle}\n\nHistorical P&L already accounted for stays.`,
      tone: "danger",
    });
    if (!ok) return;
    await api.delete(`/subscriptions/${i.id}`);
    load();
  };

  const monthly = items.filter(i => !i.cancel_planned).reduce((s, i) =>
    s + (i.cycle === "monthly" ? i.amount : i.cycle === "quarterly" ? i.amount / 3 : i.amount / 12), 0);
  const yearly = monthly * 12;
  const dueSoon = items.filter(i => daysUntil(i.next_renewal) <= 7 && daysUntil(i.next_renewal) >= 0).length;

  return (
    <div className="p-6 lg:p-10">
      <PageHeader
        label="RECURRING SPEND"
        title="Subscriptions"
        subtitle="Track every recurring vendor — renewals, costs, and cancellations."
        action={<Btn onClick={() => setOpen("new")} data-testid="add-sub"><Plus size={14} /> Add Subscription</Btn>}
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <StatTile testId="sub-active" label="Active" value={items.filter(i => !i.cancel_planned).length} />
        <StatTile testId="sub-due-soon" label="Renewing in 7d" value={dueSoon} accent={dueSoon > 0 ? "text-orange-600" : ""} />
        <StatTile testId="sub-monthly" label="Monthly Cost" value={`₹${monthly.toFixed(0)}`} sub="Normalized" />
        <StatTile testId="sub-yearly" label="Yearly Cost" value={`₹${yearly.toFixed(0)}`} accent="text-orange-600" />
      </div>

      <Card className="!p-0">
        {items.length === 0 ? (
          <div className="p-6"><EmptyState title="No subscriptions" hint="Track your SaaS, hosting, GST API, etc." /></div>
        ) : (
          <table className="kr-table">
            <thead><tr>
              {isAdmin && (
                <th className="w-8">
                  <input type="checkbox" checked={ms.selected.length > 0 && ms.selected.length === items.length}
                    onChange={() => ms.toggleAll(items.map(x => x.id))} className="accent-orange-600" />
                </th>
              )}
              <th>Service</th><th>Renewal</th><th>Cycle</th><th>Amount</th><th>Purpose</th><th>Account</th><th>Status</th><th></th>
            </tr></thead>
            <tbody>
              {items.map(i => {
                const days = daysUntil(i.next_renewal);
                return (
                  <tr key={i.id} data-testid={`sub-row-${i.id}`}>
                    {isAdmin && (
                      <td>
                        <input type="checkbox" checked={ms.isSelected(i.id)} onChange={() => ms.toggle(i.id)} className="accent-orange-600" />
                      </td>
                    )}
                    <td>
                      <div className="font-bold flex items-center gap-2">
                        {i.name}
                        {i.link && (
                          <a href={i.link} target="_blank" rel="noreferrer" className="text-orange-600 hover:text-orange-800">
                            <ArrowSquareOut size={12} weight="bold" />
                          </a>
                        )}
                      </div>
                    </td>
                    <td className="font-mono text-xs">
                      {i.next_renewal}
                      {days >= 0 && days <= 7 && (
                        <Badge tone="orange"> in {days}d</Badge>
                      )}
                      {days < 0 && <Badge tone="red"> overdue</Badge>}
                    </td>
                    <td><Badge tone="zinc">{i.cycle}</Badge></td>
                    <td className="font-mono font-bold">₹{i.amount.toLocaleString("en-IN")}</td>
                    <td className="text-xs text-zinc-600 max-w-xs truncate">{i.purpose}</td>
                    <td className="text-xs font-mono">{i.linked_account}</td>
                    <td>
                      {i.cancel_planned
                        ? <Badge tone="red">Cancel</Badge>
                        : <Badge tone="green">Active</Badge>}
                    </td>
                    <td className="flex gap-2">
                      <button onClick={() => setOpen(i)} className="text-orange-600"><PencilSimple size={14} weight="bold" /></button>
                      <button onClick={() => del(i)} className="text-red-600"><Trash size={14} weight="bold" /></button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Card>

      {open && <SubModal sub={open === "new" ? null : open} onClose={() => setOpen(null)} onSaved={() => { setOpen(null); load(); }} />}
      <MultiSelectBar
        count={ms.selected.length}
        ids={ms.selected}
        resource="subscriptions"
        label="subscriptions"
        onCleared={() => { ms.clear(); load(); }}
      />
    </div>
  );
}

function SubModal({ sub, onClose, onSaved }) {
  const isNew = !sub;
  const [f, setF] = useState(sub || {
    name: "", amount: "", currency: "INR", cycle: "monthly",
    next_renewal: new Date().toISOString().slice(0, 10),
    link: "", purpose: "", linked_account: "", cancel_planned: false, notes: "",
  });
  const upd = (k, v) => setF({ ...f, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    try {
      const body = { ...f, amount: parseFloat(f.amount) };
      if (isNew) await api.post("/subscriptions", body);
      else await api.put(`/subscriptions/${sub.id}`, body);
      toast.success(isNew ? "Added" : "Updated");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <Modal open onClose={onClose} title={isNew ? "Add Subscription" : `Edit · ${sub.name}`} size="lg">
      <form onSubmit={submit} className="grid grid-cols-2 gap-4" data-testid="sub-form">
        <Field label="Service Name" full><Input value={f.name} onChange={e => upd("name", e.target.value)} required data-testid="sub-name" /></Field>
        <Field label="Amount"><MoneyInput value={f.amount} onChange={e => upd("amount", e.target.value)} min={0} required placeholder="₹0.00" className="w-full px-3 py-2 border border-zinc-300 text-sm font-mono" data-testid="sub-amount" /></Field>
        <Field label="Billing Cycle">
          <Select value={f.cycle} onChange={e => upd("cycle", e.target.value)} data-testid="sub-cycle">
            <option value="monthly">Monthly</option>
            <option value="quarterly">Quarterly</option>
            <option value="yearly">Yearly</option>
          </Select>
        </Field>
        <Field label="Next Renewal Date"><DatePicker value={f.next_renewal} onChange={e => upd("next_renewal", e.target.value)} required data-testid="sub-renewal" /></Field>
        <Field label="Subscription Link"><Input type="url" value={f.link} onChange={e => upd("link", e.target.value)} placeholder="https://..." data-testid="sub-link" /></Field>
        <Field label="Purpose" full><Input value={f.purpose} onChange={e => upd("purpose", e.target.value)} placeholder="What is it used for?" data-testid="sub-purpose" /></Field>
        <Field label="Linked Account / Email"><Input value={f.linked_account} onChange={e => upd("linked_account", e.target.value)} data-testid="sub-account" /></Field>
        <Field label="Plan to Cancel?">
          <Select value={f.cancel_planned ? "yes" : "no"} onChange={e => upd("cancel_planned", e.target.value === "yes")} data-testid="sub-cancel">
            <option value="no">No — keep active</option>
            <option value="yes">Yes — cancel</option>
          </Select>
        </Field>
        <Field label="Notes" full><Input value={f.notes} onChange={e => upd("notes", e.target.value)} /></Field>
        <div className="col-span-2 flex justify-end gap-2">
          <Btn type="button" variant="secondary" onClick={onClose}>Cancel</Btn>
          <Btn type="submit" data-testid="sub-submit">{isNew ? "Add" : "Save"}</Btn>
        </div>
      </form>
    </Modal>
  );
}
